import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@components/button';
import { SelectMulti, SelectOption } from '@components/selectmulti';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';

import { api } from 'api';

import { Loading } from 'components/Loading';
import { Modal, ModalProps } from 'features/modals';
import { sortBy } from 'helpers/sorting';
import { useStore } from 'models/Provider';
import { IUser } from 'models/types';

type Props = ModalProps<{
  applicationId: number;
}>;

function AddApplicationAccessModal(props: Props) {
  const { applicationId, closeModal } = props;
  const { applicationStore, userStore, t } = useStore();
  const { showSuccess, showError } = useToaster();
  const applicationQuery = useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => api.applications.loadFromDb(applicationId),
  });

  const jobQuery = useQuery({
    queryKey: ['job', applicationId],
    queryFn: () =>
      applicationQuery.isSuccess && applicationQuery.data.job
        ? api.jobs.loadFromDbUnhandled(applicationQuery.data.job)
        : null,
    enabled: applicationQuery.isSuccess && applicationQuery.data.job !== null,
  });

  const [selected, setSelected] = useState<SelectOption[]>([]);

  const submit = useMutation(
    () =>
      api.applications.addFollowers(
        applicationId,
        selected.map((option) => Number(option.value))
      ),
    {
      onSuccess: () => {
        showSuccess(t('common.messages.success'));
        applicationStore.load(applicationId); // Applicant detail isn't using react-query, remove when it does
        const selectedIds = selected.map((option) => Number(option.value));
        const users = userStore.list.filter((user: IUser) => selectedIds.includes(user.id));
        applicationStore.grantFollowerAccess(applicationId, users);
        closeModal();
      },
      onError: () => {
        showError(t('application.grantmultifollowers.error'));
      },
    }
  );

  if (!applicationQuery.isSuccess || !jobQuery.isSuccess) {
    return <Loading />;
  }

  const application = applicationQuery.data;
  const job = jobQuery.data;
  const applicationFollowers = application.followers.map((follower) => follower.id);
  const jobFollowers = job!.followers.map((follower) => follower.id);
  const users: IUser[] = usersWithoutSelected();

  function usersWithoutSelected() {
    const users = userStore.list.filter((user: IUser) => {
      const selectedIds = selected.map((option) => Number(option.value));
      if (
        user.role === 'user' ||
        selectedIds.includes(user.id) ||
        applicationFollowers.includes(user.id) ||
        jobFollowers.includes(user.id)
      ) {
        return false;
      }
      return true;
    });
    return sortBy(users, 'name');
  }

  return (
    <Modal>
      <Modal.Header text={t('application.access.addmodal.header')} />
      {submit.isLoading && <Loading />}

      <Text text={t('application.access.addmodal.maintext')} />

      <SelectMulti
        placeholder={t('application.access.addmodal.select')}
        label={t('common.select')}
        options={users.map((u) => ({ label: u.name, helpText: u.email, value: u.id.toString() }))}
        onChange={setSelected}
        noOptions={() => t('multiselect.nooptions')}
      />

      <Modal.Actions>
        <Button
          variant="secondary"
          onClick={closeModal}
          isDisabled={submit.isLoading}
          text={t('application.access.addmodal.button.cancel')}
        />
        <Button
          onClick={() => submit.mutate()}
          isDisabled={submit.isLoading || selected.length === 0}
          text={t('application.access.addmodal.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
}

export default AddApplicationAccessModal;
